import { AntDesign, MaterialIcons } from "@expo/vector-icons";
import Clipboard from "@react-native-clipboard/clipboard";
import { capitalCase } from "change-case";
import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { View, Pressable, Text } from "react-native";
import { RadioButton } from "react-native-paper";
import QRCode from "react-native-qrcode-svg";

import Subtitle from "@/components/Subtitle";
import Title from "@/components/Title";
import Button from "@/components/button/Button";
import InputChoose from "@/components/input/InputChoose";
import { sendRequest } from "@/utils/ApiUtil";
import {
  BridgeLiquidationAddressChain,
  BridgeVirtualAccountPaymentCurrency,
} from "@/utils/dto/extracted_enums";
import { truncateMiddle } from "@/utils/text/FormatTextUtils";

interface Instructions {
  [key: string]: string;
}

interface FiatDepositProps {
  depositInstructions: Instructions[];
  hasToClearStates?: boolean;
  clearedStates?: (state: boolean) => void;
}

interface CryptoDepositProps {
  address: string;
  blockchain: string;
  network: string;
  hasToClearStates?: boolean;
  clearedStates?: (state: boolean) => void;
}

interface CreateCryptoAddressToFiatProps {
  accountId: string;
  onError?: () => void;
}

const addressKeys = ["institutionAddress", "accountHolderAddress"];

export const copyToClipboard = (data: any, parentKey?: string) => {
  if (typeof data === "string") {
    Clipboard.setString(data);
    return;
  }
  const buildString = (obj: any, parentKey?: string): string =>
    Object.entries(obj)
      .map(([key, value]) => {
        if (typeof value === "object" && value !== null) {
          return buildString(value, key);
        }
        return `${parentKey ? `${capitalCase(parentKey)} - ` : ""}${capitalCase(
          key
        )}: ${value}`;
      })
      .join("\n");

  const finalString = buildString(data, parentKey);
  Clipboard.setString(finalString);
};

const renderAddress = (addressKey: string, addressValue: any) => {
  return (
    <View
      className="flex-row my-1 w-full justify-between"
      key={`${addressKey}`}
    >
      <Text
        numberOfLines={1}
        ellipsizeMode="tail"
        className="text-base font-semibold max-w-56"
      >
        {capitalCase(addressKey)}:
      </Text>
      <Text
        numberOfLines={1}
        ellipsizeMode="tail"
        className="text-base max-w-40"
      >
        {`${addressValue.addressLine1}, ${addressValue.city} - ${addressValue.state} - ${addressValue.postalCode} ${addressValue.countryCode}`}
      </Text>
    </View>
  );
};

export const mapToRenderDepositInstructions = (
  data: any,
  keysToIgnore: string[],
  isCryptoDeposit: boolean = false,
  parentKey?: string
) => {
  return Object.entries(data)
    .filter(([key]) => !keysToIgnore.includes(key))
    .flatMap(([key, value]) => {
      const formattedKey = parentKey
        ? `${capitalCase(parentKey)} - ${capitalCase(key)}`
        : capitalCase(key);

      if (
        typeof value === "object" &&
        addressKeys.includes(key) &&
        value !== null &&
        !Array.isArray(value)
      ) {
        return renderAddress(key, value);
      } else if (
        typeof value === "object" &&
        !addressKeys.includes(key) &&
        value !== null &&
        !Array.isArray(value)
      ) {
        return mapToRenderDepositInstructions(value, keysToIgnore, false, key);
      }

      return (
        <View
          className="flex-row my-1 w-full justify-between"
          key={formattedKey}
        >
          <Text
            numberOfLines={1}
            ellipsizeMode="tail"
            className="text-base font-semibold max-w-56"
          >
            {formattedKey}:
          </Text>
          <Text
            numberOfLines={1}
            className={`text-base text-end ${
              isCryptoDeposit ? "max-w-64" : "max-w-40"
            }`}
          >
            {truncateMiddle(String(value))}
          </Text>
        </View>
      );
    });
};

export const RenderCryptoDeposit = (props: CryptoDepositProps) => {
  const [isCopied, setIsCopied] = useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    if (props.hasToClearStates) {
      setIsCopied(false);
      props.clearedStates(false);
    }
  }, [props.hasToClearStates]);

  function propsToObject(props: CryptoDepositProps) {
    return {
      address: props.address,
      blockchain: props.blockchain.toUpperCase(),
    };
  }

  return (
    <View className="w-full items-center flex-grow">
      <Title text={t("home/bankterms:cryptodeposit")} />
      <View className="w-full items-center">
        <Text className="text-sm text-center font-light my-2">
          {t("home/home:cryto.alert")}
        </Text>
      </View>
      <View className="flex-grow items-center justify-center my-2 w-full">
        <View className="w-full items-center m-1">
          <QRCode
            value={`${props.blockchain}:${props.address}`}
            size={120}
            logoBackgroundColor="transparent"
          />
          <Text className="text-xs text-darkergray mt-0.5">
            {t("home/bankterms:walletaddress")}
          </Text>
        </View>
        {mapToRenderDepositInstructions(propsToObject(props), [], true)}
        <Pressable
          className="flex-row items-center mt-4"
          onPress={() => {
            setIsCopied(true);
            copyToClipboard(props.address);
          }}
        >
          {isCopied ? (
            <AntDesign name="check" size={24} color="green" />
          ) : (
            <MaterialIcons name="content-copy" size={24} color="black" />
          )}
          <Text className="font-semibold m-1">
            {t("common:placeholder.copytoclipboard")}
          </Text>
        </Pressable>
      </View>
    </View>
  );
};

export const RenderFiatDeposit = (props: FiatDepositProps) => {
  const [transferType, setTransferType] = useState("");
  const [isCopied, setIsCopied] = useState(false);
  const { t } = useTranslation();
  const instructionsKeysToIgnore = ["instructionType", "assetTypeId"];

  const instructions = props.depositInstructions
    ? props.depositInstructions.find(
        (instruction) => instruction.instructionType === transferType
      )
    : [];

  return (
    <View className="w-full items-center flex-grow">
      <View className="mt-1 gap-2">
        <Title text={t("common:modal.depositinformations.title")} />
        <Subtitle text={t("common:modal.depositinformations.subtitle")} />
      </View>
      {!transferType ? (
        <View className="flex-col justify-center grow">
          <View className="flex-row items-center">
            <RadioButton
              value="Swift"
              status={transferType === "SWIFT" ? "checked" : "unchecked"}
              onPress={() => setTransferType("SWIFT")}
              color="#5D0CBA"
            />
            <Text className="text-lg">Swift</Text>
          </View>
          <View className="flex-row items-center">
            <RadioButton
              value="Fedwire"
              status={transferType === "FEDWIRE" ? "checked" : "unchecked"}
              onPress={() => setTransferType("FEDWIRE")}
              color="#5D0CBA"
            />
            <Text className="text-lg">Fedwire</Text>
          </View>
          <View className="flex-row items-center">
            <RadioButton
              value="ACH"
              status={transferType === "ACH" ? "checked" : "unchecked"}
              onPress={() => setTransferType("ACH")}
              color="#5D0CBA"
            />
            <Text className="text-lg">ACH</Text>
          </View>
        </View>
      ) : (
        <View className="flex-col w-full p-2 items-center justify-center flex-grow">
          {mapToRenderDepositInstructions(
            instructions ?? {},
            instructionsKeysToIgnore
          )}
          <Pressable
            className="flex-row items-center mt-4"
            onPress={() => {
              setIsCopied(true);
              copyToClipboard(instructions);
            }}
          >
            {isCopied ? (
              <AntDesign name="check" size={24} color="green" />
            ) : (
              <MaterialIcons name="content-copy" size={24} color="black" />
            )}
            <Text className="font-semibold m-1">
              {t("common:placeholder.copytoclipboard")}
            </Text>
          </Pressable>
        </View>
      )}
    </View>
  );
};

export const CreateCryptoAddressToFiat = (
  props: CreateCryptoAddressToFiatProps
) => {
  const [paymentRail, setPaymentRail] = useState("");
  const [currency, setCurrency] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [cryptoAddress, setCryptoAddress] = useState<any>({});
  const { t } = useTranslation();

  const { control } = useForm<any>({
    mode: "onSubmit",
  });

  const fetchWalletAddress = async (
    accountId: string,
    payment_rail: string,
    currency: string
  ) => {
    setSubmitting(true);
    try {
      const request = await sendRequest(
        "get",
        "/bridge/liquidation-address",
        {
          accountId,
          payment_rail,
          currency,
        },
        true
      );
      if (request) {
        setSubmitted(true);
        setCryptoAddress({
          address: request.address,
          blockchain: request.chain,
          network: request.chain,
        });
      }
    } catch (error) {
      console.log(error);
      props.onError();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <View className="w-full items-center flex-grow">
      {!submitted && (
        <View className="mt-2 items-center">
          <View className="mt-1 gap-2 w-full items-center">
            <Title text={t("home/modals/depositmodal:cryptotofiat.title")} />
            <Subtitle
              text={t("home/modals/depositmodal:cryptotofiat.subtitle")}
            />
          </View>
          <View className="z-50">
            <InputChoose
              label={t("home/modals/depositmodal:cryptotofiat.input.chain")}
              control={control}
              handleChange={(data) => setPaymentRail(data)}
              data={{ BridgeLiquidationAddressChain }}
              name="paymentRail"
            />
          </View>
          <View className="z-40">
            <InputChoose
              label={t("home/modals/depositmodal:cryptotofiat.input.currency")}
              control={control}
              handleChange={(data) => setCurrency(data)}
              data={{ BridgeVirtualAccountPaymentCurrency }}
              name="currency"
            />
          </View>
          <Button
            label={t("common:button.createwalletaddress")}
            onClick={() =>
              fetchWalletAddress(props.accountId, paymentRail, currency)
            }
            isLoading={submitting}
          />
        </View>
      )}
      {submitted && (
        <RenderCryptoDeposit
          address={cryptoAddress?.address ?? ""}
          blockchain={cryptoAddress?.blockchain ?? ""}
          network=""
        />
      )}
    </View>
  );
};
